import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { notificationSchema } from "../../Schema/generalSchema";
import Button from "../../Components/Button";
import { useMutation } from "react-query";
import { sendBroadcastNotification } from "../../Services/Notification";
import { Error, Success } from "../../Components/Modal";
import BackButton from "../../Components/BackButton";
import { useState } from "react";
import "../CMSManagement/CMSManagement.css";
import "./AddNotification.css";

const TARGET_OPTIONS = [
    { value: "all", label: "Broadcast All", icon: "fa-bullhorn", hint: "Users & Drivers" },
    { value: "user", label: "Users Only", icon: "fa-user", hint: "User app" },
    { value: "mover", label: "Drivers Only", icon: "fa-truck", hint: "Driver app" },
];

const TARGET_LABELS = {
    all: "Everyone",
    user: "Users",
    mover: "Drivers",
};

export default function AddNotification() {
    const navigate = useNavigate();
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: yupResolver(notificationSchema),
        defaultValues: { target: "all" },
    });

    const watchTarget = watch("target", "all");
    const watchTitle = watch("title", "");
    const watchBody = watch("body", "");

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const clearImage = () => {
        setImage(null);
        setPreview(null);
    };

    const { mutate, isLoading } = useMutation(
        (data) => sendBroadcastNotification(data),
        {
            onSuccess: (res) => {
                Success("Campaign Sent", res.data?.message || "Notifications have been successfully broadcasted.");
                navigate("/notification-management");
            },
            onError: (err) => {
                Error(err?.response?.data?.message || "Failed to send notifications.");
            },
        }
    );

    const submit = (data) => {
        const formData = new FormData();
        formData.append("typeName", "notifications");
        formData.append("title", data.title);
        formData.append("body", data.body);
        formData.append("target", data.target);
        if (image) formData.append("image", image);
        mutate(formData);
    };

    return (
        <div className="app-content content campaign-page">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="premium-notification-hub">
                        <div className="user-details-wrapper">
                            <div className="campaign-page-header">
                                <div className="back-title d-flex align-items-center mb-2">
                                    <BackButton />
                                    <h1 className="cms-page-header__title ms-3">Launch Push Campaign</h1>
                                </div>
                                <p className="cms-page-header__subtitle ms-5 ps-2">
                                    Design and broadcast notifications to users and drivers
                                </p>
                            </div>

                            <form onSubmit={handleSubmit(submit)}>
                                <div className="campaign-layout">
                                    <div className="campaign-main">
                                        <div className="campaign-card">
                                            <div className="campaign-card__head">
                                                <span className="campaign-card__step">1</span>
                                                <div>
                                                    <h5 className="campaign-card__title">Audience Targeting</h5>
                                                    <p className="campaign-card__desc">Choose who receives this notification</p>
                                                </div>
                                            </div>
                                            <div className="campaign-target-grid">
                                                {TARGET_OPTIONS.map((opt) => {
                                                    const isSelected = watchTarget === opt.value;
                                                    return (
                                                        <label
                                                            key={opt.value}
                                                            className={`campaign-target-option ${isSelected ? "is-selected" : ""}`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                className="d-none"
                                                                value={opt.value}
                                                                {...register("target")}
                                                            />
                                                            <span className="campaign-target-option__inner">
                                                                <span className="campaign-target-option__icon">
                                                                    <i className={`fas ${opt.icon}`} />
                                                                </span>
                                                                <span className="campaign-target-option__label">{opt.label}</span>
                                                                <span className="campaign-target-option__hint">{opt.hint}</span>
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="campaign-card">
                                            <div className="campaign-card__head">
                                                <span className="campaign-card__step">2</span>
                                                <div>
                                                    <h5 className="campaign-card__title">Campaign Message</h5>
                                                    <p className="campaign-card__desc">Write headline, body, and optional banner</p>
                                                </div>
                                            </div>

                                            <div className="campaign-message-grid">
                                                <div className="campaign-message-fields">
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <label className="cms-field-label mb-0">
                                                                Headline <span className="text-danger">*</span>
                                                            </label>
                                                            <span className={`campaign-char-count ${watchTitle.length > 55 ? "is-warning" : ""}`}>
                                                                {watchTitle.length} / 65
                                                            </span>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            className={`cms-input ${errors.title ? "is-invalid" : ""}`}
                                                            placeholder="Headline that grabs attention..."
                                                            maxLength={65}
                                                            {...register("title")}
                                                        />
                                                        {errors.title && (
                                                            <span className="cms-field-error">{errors.title.message}</span>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <label className="cms-field-label mb-0">
                                                                Message Body <span className="text-danger">*</span>
                                                            </label>
                                                            <span className={`campaign-char-count ${watchBody.length > 220 ? "is-warning" : ""}`}>
                                                                {watchBody.length} / 250
                                                            </span>
                                                        </div>
                                                        <textarea
                                                            className={`cms-textarea ${errors.body ? "is-invalid" : ""}`}
                                                            placeholder="Keep it short, actionable and punchy..."
                                                            maxLength={250}
                                                            rows={3}
                                                            {...register("body")}
                                                        />
                                                        {errors.body && (
                                                            <span className="cms-field-error">{errors.body.message}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="campaign-banner-col">
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <label className="cms-field-label mb-0">Banner (Optional)</label>
                                                        {preview && (
                                                            <button type="button" className="campaign-banner-remove" onClick={clearImage}>
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div
                                                        className={`campaign-banner-upload${preview ? " campaign-banner-upload--has-preview" : ""}`}
                                                        onClick={() => document.getElementById("notif-banner").click()}
                                                    >
                                                        {preview ? (
                                                            <>
                                                                <img src={preview} alt="Campaign banner" />
                                                                <div className="campaign-banner-upload__overlay">
                                                                    <i className="fas fa-camera me-2" /> Change
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="campaign-banner-upload__placeholder">
                                                                <div className="campaign-banner-upload__icon">
                                                                    <i className="fas fa-image" />
                                                                </div>
                                                                <span className="campaign-banner-upload__text">Upload Banner</span>
                                                                <span className="campaign-banner-upload__hint">1200×600</span>
                                                            </div>
                                                        )}
                                                        <input
                                                            type="file"
                                                            id="notif-banner"
                                                            hidden
                                                            accept="image/*"
                                                            onChange={handleImageChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <aside className="campaign-preview-panel">
                                        <div className="campaign-preview-card">
                                            <div className="campaign-preview-card__label">Live Preview</div>
                                            <div className="campaign-preview-phone">
                                                <div className="campaign-preview-notification">
                                                    <div className="campaign-preview-notification__banner">
                                                        {preview ? (
                                                            <img src={preview} alt="Preview" />
                                                        ) : (
                                                            <i className="fas fa-image" />
                                                        )}
                                                    </div>
                                                    <div className="campaign-preview-notification__body">
                                                        <div className="campaign-preview-notification__app">
                                                            <i className="fas fa-bell" />
                                                            MUVRRS
                                                        </div>
                                                        <h6 className="campaign-preview-notification__title">
                                                            {watchTitle || "Notification Title"}
                                                        </h6>
                                                        <p className="campaign-preview-notification__message">
                                                            {watchBody || "Your message will appear here..."}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="campaign-preview-stats">
                                                <div className="campaign-preview-stat">
                                                    <span className="campaign-preview-stat__label">Target</span>
                                                    <span className="campaign-preview-stat__badge">
                                                        {TARGET_LABELS[watchTarget] || "Everyone"}
                                                    </span>
                                                </div>
                                                <div className="campaign-preview-stat">
                                                    <span className="campaign-preview-stat__label">Headline</span>
                                                    <span className="campaign-preview-stat__value">{watchTitle.length} chars</span>
                                                </div>
                                                <div className="campaign-preview-stat">
                                                    <span className="campaign-preview-stat__label">Body</span>
                                                    <span className="campaign-preview-stat__value">{watchBody.length} chars</span>
                                                </div>
                                                <div className="campaign-preview-stat">
                                                    <span className="campaign-preview-stat__label">Banner</span>
                                                    <span className="campaign-preview-stat__value">{preview ? "Attached" : "None"}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </aside>
                                </div>

                                <div className="campaign-footer campaign-footer--actions">
                                    <div className="campaign-footer__note d-none d-lg-flex">
                                        <div className="campaign-footer__note-icon">
                                            <i className="fas fa-shield-alt" />
                                        </div>
                                        <div>
                                            <strong>Secure Broadcast</strong>
                                            <span>Encrypted delivery to selected audience</span>
                                        </div>
                                    </div>
                                    <div className="campaign-footer__actions">
                                        <button
                                            type="button"
                                            className="cms-btn-cancel"
                                            onClick={() => navigate(-1)}
                                        >
                                            Cancel
                                        </button>
                                        <Button className="cms-btn-save" type="submit" loading={isLoading}>
                                            {isLoading ? (
                                                <i className="fas fa-circle-notch fa-spin" />
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane me-2" />
                                                    Launch
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
